import { createSlice, createSelector } from '@reduxjs/toolkit';
import { streamsListSelector } from './streamingSlice';
import { trafficFeaturesSelector } from './liveTrafficSlice';
import { unitsSelector } from './globalSlice';
import { getLiveTrafficTableColumns } from 'components/airSpace/airspaceUtils';
import { userLocationFeaturesFilteredByMapBoundsSelector } from './mapSlice';
import isEqual from 'lodash/isEqual';

export const RIGHT_PANEL_STATES = {
	DRAWER_OPEN: 'drawer_open',
	POPOUT_OPEN: 'popout_open',
	CLOSED: 'closed'
};

const opsViewSlice = createSlice({
	name: 'opsView',
	initialState: {
		streamingView: RIGHT_PANEL_STATES.DRAWER_OPEN,
		stateHydrated: false,
		showInMap: [],
		showInPanel: [],
		trafficPanelOpen: false,
		playingStreams: []
	},
	reducers: {
		setStreamingDrawerOpen(state) {
			state.streamingView = RIGHT_PANEL_STATES.DRAWER_OPEN;
		},
		setStreamingPopoutOpen(state) {
			state.streamingView = RIGHT_PANEL_STATES.POPOUT_OPEN;
		},
		setStreamingClosed(state) {
			state.streamingView = RIGHT_PANEL_STATES.CLOSED;
		},
		setStateHydrated(state, action) {
			state.stateHydrated = action.payload;
		},
		setShowInMap(state, action) {
			state.showInMap = action.payload;
		},
		setShowInPanel(state, action) {
			state.showInPanel = action.payload;
		},
		setTrafficPanelOpen(state, action) {
			state.trafficPanelOpen = action.payload;
		},
		setPlayingStreams(state, action) {
			state.playingStreams = action.payload;
		}
	}
});
export const opsViewActions = Object.values(opsViewSlice.actions).map(action => action.type);

export const {
	setStreamingDrawerOpen,
	setStreamingPopoutOpen,
	setStreamingClosed,
	setStateHydrated,
	setShowInMap,
	setShowInPanel,
	setTrafficPanelOpen,
	setPlayingStreams
} = opsViewSlice.actions;
export default opsViewSlice.reducer;

export const streamingViewSelector = state => state.opsView.streamingView;
export const stateHydratedSelector = state => state.opsView.stateHydrated;
export const showInMapSelector = state => state.opsView.showInMap;
export const showInPanelSelector = state => state.opsView.showInPanel;
export const trafficPanelOpenSelector = state => state.opsView.trafficPanelOpen;
export const playingStreamsSelector = state => state.opsView.playingStreams;

let lastTrafficInput = null;
let lastTrafficResult = null;

// Create selector lazily to avoid circular dependency issues
export const aircraftFormattedForMapSelector = (() => {
	let selector = null;
	return state => {
		if (!selector) {
			selector = createSelector([trafficFeaturesSelector, unitsSelector], (aircraft, units) => {
				const previewProps = getLiveTrafficTableColumns({ isForPreviewProp: true, units });
				if (lastTrafficInput && isEqual(aircraft, lastTrafficInput)) {
					return lastTrafficResult;
				}
				lastTrafficInput = aircraft;
				lastTrafficResult = aircraft.map(f => ({
					...f,
					preview_properties: previewProps
				}));
				return lastTrafficResult;
			});
		}
		return selector(state);
	};
})();

export const usersWithMatchedStreamsInternalSelector = createSelector(
	[userLocationFeaturesFilteredByMapBoundsSelector, streamsListSelector],
	(users, streams) => {
		const matchedStreamIds = new Set();
		const streamByUserId = new Map();

		// First, identify streams that are matched to aircraft
		const aircraftMatchedStreamIds = new Set();
		(streams || []).forEach(s => {
			if (s.properties.aircraft?.internal_serial_number != null) {
				aircraftMatchedStreamIds.add(s.id);
			}
		});

		// Then map user streams, excluding those matched to aircraft
		(streams || []).forEach(s => {
			if (s.properties.user.id != null && !aircraftMatchedStreamIds.has(s.id)) {
				streamByUserId.set(s.properties.user.id, s);
			}
		});

		const results = users.map(user => {
			const stream = streamByUserId.get(user.properties.user_id);
			if (stream) {
				matchedStreamIds.add(stream.id);
				return {
					...user,
					stream,
					properties: {
						...user.properties,
						object_type: 'userStream',
						ops_view_id: `user-${user.properties.user_id}`
					}
				};
			} else {
				return {
					...user,
					properties: {
						...user.properties,
						object_type: 'userOnly',
						ops_view_id: `user-${user.properties.user_id}`
					}
				};
			}
		});
		return { results, matchedStreamIds };
	}
);

export const usersWithMatchedStreamsSelector = createSelector(
	[usersWithMatchedStreamsInternalSelector],
	data => data.results
);

export const matchedUserStreamIdsSelector = createSelector(
	[usersWithMatchedStreamsInternalSelector],
	data => data.matchedStreamIds
);

export const aircraftWithMatchedStreamsInternalSelector = createSelector(
	[aircraftFormattedForMapSelector, streamsListSelector],
	(aircraftList, streams) => {
		const matchedStreamIds = new Set();
		const streamByAircraftId = new Map();
		(streams || []).forEach(stream => {
			if (stream.properties.aircraft?.internal_serial_number != null) {
				streamByAircraftId.set(stream.properties.aircraft?.internal_serial_number, stream);
			}
		});
		const results = aircraftList.map(aircraft => {
			const stream = streamByAircraftId.get(aircraft.provider_id);
			if (stream) {
				matchedStreamIds.add(stream.id);
				return {
					...aircraft,
					stream,
					properties: {
						...aircraft.properties,
						object_type: 'aircraftStream',
						ops_view_id: `aircraft-${aircraft.id}`
					}
				};
			} else {
				return {
					...aircraft,
					properties: {
						...aircraft.properties,
						object_type: 'aircraftOnly',
						ops_view_id: `aircraft-${aircraft.id}`
					}
				};
			}
		});
		return { results, matchedStreamIds };
	}
);

export const aircraftWithMatchedStreamsSelector = createSelector(
	[aircraftWithMatchedStreamsInternalSelector],
	data => data.results
);

export const matchedAircraftStreamIdsSelector = createSelector(
	[aircraftWithMatchedStreamsInternalSelector],
	data => data.matchedStreamIds
);

export const unmatchedStreamsSelector = createSelector(
	[streamsListSelector, matchedUserStreamIdsSelector, matchedAircraftStreamIdsSelector],
	(streams, userIds, aircraftIds) => {
		const matchedIds = new Set([...userIds, ...aircraftIds]);
		return streams
			?.filter(stream => !matchedIds.has(stream.id))
			.map(stream => ({
				...stream,
				properties: {
					...stream.properties,
					object_type: 'streamOnly',
					ops_view_id: `stream-${stream.id}`
				}
			}));
	}
);

export const opsViewFeaturesSelector = createSelector(
	[usersWithMatchedStreamsSelector, aircraftWithMatchedStreamsSelector, unmatchedStreamsSelector],
	(users, aircraft, unmatched) => [
		...users,
		...aircraft
		// ...unmatched  // Uncomment if needed
	]
);
